/* eslint-disable no-param-reassign */
/**
 * Information extracted from event target
 * @typedef {Object} TargetData
 * @property {boolean} intOnly indicates whether input only allows integer values
 * @property {boolean} autoTab indicates whether input will perform autotab
 * @property {number} currentLength the length of the input value (char count)
 * @property {number} maxLength the maximum allowed length of the input value
 * @property {string} inputType the input type like "number" | "text" | "button"
 * @property {string} value in case of input event in number or string inputs this will be value
 */
/**
 * Information extracted from the event object
 * @typedef {Object} EventData
 * @property {string|undefined} data in case of input event, the data contains the input data
 * @property {string|undefined} key in case of Keyboard Event, this will contain the key char
 * @property {number|undefined} keyCode in case of Keyboard Event, this will contain the key code
 * @property {boolean|undefined} metaKey in case of Keyboard Event, this will indicate thether metaKey was pressed
 * @property {(event?: Event) => void} preventDefault the prevent Default function to terminate event processing
 * @property {KeyboardEvent | ClipboardEvent | InputEvent} event the event object
 * @property {HTMLInputElement} target the event target input element triggered the event
 */
/**
 * Event Data composed by the wrapper
 *
 * @typedef {EventData & TargetData} EventHandlerOptions
 */

window.o_global.eventQBus.on("assets.formular.init", (selector = "") => {
  const inputPSelector = `${selector} .js-p_input input`;
  const inputPlSelector = `${selector} .js-pl_input input`;
  const inputElements = document.querySelectorAll(`${inputPSelector},${inputPlSelector}`);

  /**
   *
   * @param {string|number} value the value to be checked
   * @param {boolean} intOnly indicates whether value must be an integer
   * @returns {null|number} either parsed number or null
   */
  const checkValue = (value, intOnly) => {
    // make val absolute. in case of bas values will end up in NaN
    // if value must be integer, we parse it to int first
    const absoluteValue = Math.abs(intOnly ? parseInt(value, 10) : value);

    // Only accept positive finite numbers but only if value is valid
    // in case of invalid value target.value will be empty string
    const validValue =
      !!value && absoluteValue >= 0 && absoluteValue < Infinity ? absoluteValue.toString() : null;

    // if value was entered with leading zero, add that again
    return !!validValue && validValue.length < value.length ? `0${validValue}` : validValue;
  };

  /**
   * this is used to extract interesting data from target element
   * @param {HTMLInputElement} target the html element triggered the event
   * @returns {TargetData}
   */
  const analyzeTarget = (target) => {
    const autoTab = target.parentNode.hasAttribute("data-autotab");
    const intOnly = target.parentNode.hasAttribute("data-int");
    const currentLength = target.value.length;
    const inputType = target.type;
    const maxLength = parseInt(target.getAttribute("maxlength"), 10);

    return {
      autoTab,
      currentLength,
      inputType,
      maxLength,
      intOnly,
      value: target.value,
    };
  };

  /**
   * @type {Record<string, (params: EventHandlerOptions) => void>}
   */
  const eventMap = {
    input: ({ currentLength, maxLength, target, autoTab, inputType, intOnly, data, value }) => {
      // Sanitize non empty number
      // This is just an ass protection against weirdness
      if (inputType === "number") {
        // in case of incomplete input like 5e, 2. etc data
        // will contain hint about users intention
        if ([".", ","].includes(data) && intOnly) {
          // forbid decimal sign for int only.
          // This hack removes the decimal sign from element
          target.value = null;
          target.value = value;
        }

        // We check the value before further processing it
        // this will force conversion into number
        const resultValue = checkValue(value, intOnly);

        target.value = resultValue;
      }

      if (currentLength >= maxLength) {
        // Input value is longer, than allowed
        // We trim value to allowed length
        const allowed = target.value.substring(0, maxLength);
        // Do not lose overtyped chars
        const rest = target.value.replace(allowed, "");
        // Ensure max length on current target
        target.value = allowed;

        if (autoTab) {
          // Process autotab
          const { form } = target;
          const index = Array.prototype.indexOf.call(form, target);
          const next = form.elements[index + 1];

          // Only process next if next field is empty
          if (next && !next.value) {
            // The call order is magic the focus will trigger blur
            next.focus();
            // if there is a rest from copy paste for example
            // then pass it further to the next element
            if (rest) {
              next.value = rest;
              // This MUST be the last calls in the function due to "var"s in the transpiled
              // output (weird js)
              eventMap.input({ ...analyzeTarget(next), target: next });
            }
          }
        }
      }
    },

    paste: ({ event, inputType, preventDefault, intOnly }) => {
      if (inputType === "number") {
        // only allow numeric values to be pasted
        const val = checkValue(
          (event.clipboardData || window.clipboardData).getData("text"),
          intOnly
        );
        if (!val) {
          // paste data is bad, val is null so prevent paste
          // the pasted data will be handled on input event
          preventDefault.call(event);
        }
      }
    },

    blur: ({ maxLength, target, inputType, intOnly, value }) => {
      // Sanitise non empty number
      if (inputType === "number") {
        const resultValue = checkValue(value, intOnly);
        // the current length is not exactly the sanitized number length
        const length = resultValue ? resultValue.toString().length : 0;

        if (maxLength === 2 && length === 1 && resultValue > 0 && resultValue <= 9) {
          // Special case for two digit inputs like dates
          target.value = `0${resultValue}`;
        } else {
          target.value = resultValue;
        }
      }
    },

    keydown: ({
      key,
      keyCode,
      currentLength,
      inputType,
      target,
      preventDefault,
      event,
      autoTab,
    }) => {
      // Do not allow Special snowflakes on number fileds
      // these chars are allowed by browser, but they break target.value
      // and prevent proper handling of input values
      if (
        inputType === "number" &&
        (["e", "+", "-"].includes(key) || [69, 107, 109].includes(keyCode))
      ) {
        preventDefault.call(event);
      }

      // in case of delete key in autotab inputs we must check if
      // the value is empty and focus previous element
      if (
        (keyCode === 8 || keyCode === 46 || key === "Delete" || key === "Backspace") &&
        autoTab &&
        currentLength <= 0
      ) {
        const { form } = target;
        const index = Array.prototype.indexOf.call(form, target);
        const prev = form.elements[index - 1];

        if (prev) {
          prev.focus();
          // We also must supress event, because otherwise
          // this will delete last character on the focused input
          preventDefault.call(event);
        }
      }
    },
  };

  /**
   * Event wrapper. Prepares the event data and invokes the corresponding event
   * @param {KeyboardEvent|ClipboardEvent|InputEvent} event
   */
  const handler = (event) => {
    const { keyCode, type, target, preventDefault, key, metaKey, data } = event;
    const { autoTab, currentLength, inputType, maxLength, intOnly, value } = analyzeTarget(target);

    eventMap[type]({
      data,
      key,
      keyCode,
      metaKey,
      currentLength,
      maxLength,
      preventDefault,
      event,
      target,
      value,
      autoTab,
      intOnly,
      inputType,
    });
  };

  [].forEach.call(inputElements, (element) => {
    Object.keys(eventMap).forEach((eventName) => {
      element.addEventListener(eventName, handler);
    });
  });
});

window.o_global.eventLoader.onAllScriptsExecuted(130, () => {
  window.o_global.eventQBus.emit("assets.formular.init", "body");
});
